/* App-shell directory invalidation — keeps Obchody + Organizace fresh when admin
 * verifies bank wiring, even if the customer tab is on /browse (no directory screen mounted).
 *
 * G10 — sole owner of tab-return (`visibilitychange` + `focus`) directory refresh;
 * screen hooks (`useShopsDirectoryRealtimeSync`, `useOrgDirectoryRealtimeSync`) must not
 * register duplicate listeners. */

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  createCoalescedRefetchScheduler,
  type CoalescedRefetchScheduler,
} from 'pi-kiosk-shared';
import {
  isHttpStormLatched,
  subscribeHttpStormLatch,
} from '../../../shared/http/httpStormDetectedLatch.js';
import {
  invalidateAllAccountMembershipsCache,
  invalidateAllKioskPickupPointsCache,
  invalidateKioskPickupPointsForSalesPoint,
  invalidateAllPromoTenantQueriesCache,
  invalidateAllSalesPointContactsCache,
  invalidateAllSurfaceEntitlementCache,
} from '../../../shared/api/customerDirectoryCacheInvalidate.js';
import {
  invalidateAllShopKioskMetaCache,
  invalidateShopKioskMetaCache,
  invalidateShopKioskMetaForSalesPoint,
} from '../../../shared/api/shopKioskMetaCacheInvalidate.js';
import {
  isKioskUpdated,
  readSalesPointIdFromRealtimeMessage,
  readTenantCodeFromBrandingMessage,
} from '../../../shared/api/shopKioskMetaRealtimeEvents.js';
import {
  patchDirectoryDisplayInCaches,
  patchDirectoryGeoInCaches,
  readDirectoryDisplayFieldsFromPayload,
  readDirectoryGeoFromPayload,
  readDirectorySalesPointId,
} from '../../../shared/api/directoryGeoCachePatch.js';
import { customerQueryKeys } from '../../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import {
  SSE_DIRECTORY_REFRESH_COOLDOWN_MS,
  VISIBILITY_DIRECTORY_REFRESH_COOLDOWN_MS,
} from './useShopsDirectoryRealtimeSync.js';
import { usePublicDirectorySseConnected } from './PublicDirectorySseProvider.js';

interface DirectoryEventMessage {
  type?: string;
  data?: Record<string, unknown>;
}

function isCustomerDirectoryUpdated(message: DirectoryEventMessage): boolean {
  if (message.type === 'customer_directory_updated') {
    return true;
  }
  return message.data?.type === 'customer_directory_updated';
}

function isTenantBrandingUpdated(message: DirectoryEventMessage): boolean {
  if (message.type === 'tenant_branding_updated') {
    return true;
  }
  return message.data?.type === 'tenant_branding_updated';
}

function isTenantPromotionsConfigUpdated(message: DirectoryEventMessage): boolean {
  if (message.type === 'tenant_promotions_config_updated') {
    return true;
  }
  return message.data?.type === 'tenant_promotions_config_updated';
}

function isPickupPointsUpdated(message: DirectoryEventMessage): boolean {
  if (message.type === 'pickup_points_updated') {
    return true;
  }
  return message.data?.type === 'pickup_points_updated';
}

function scheduleDirectoryDependentCaches(
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  invalidateAllSurfaceEntitlementCache(queryClient);
  invalidateAllKioskPickupPointsCache(queryClient);
  invalidateAllPromoTenantQueriesCache(queryClient);
}

function refreshShopKioskMetaFromDirectoryEvent(
  queryClient: ReturnType<typeof useQueryClient>,
  message: DirectoryEventMessage,
): void {
  if (isKioskUpdated(message)) {
    const salesPointId = readSalesPointIdFromRealtimeMessage(message);
    if (salesPointId !== undefined) {
      invalidateShopKioskMetaForSalesPoint(queryClient, salesPointId);
    } else {
      invalidateAllShopKioskMetaCache(queryClient);
    }
    return;
  }
  if (isTenantBrandingUpdated(message)) {
    const tenantCode = readTenantCodeFromBrandingMessage(message);
    if (tenantCode !== undefined) {
      invalidateShopKioskMetaCache(queryClient, tenantCode);
    } else {
      invalidateAllShopKioskMetaCache(queryClient);
    }
    return;
  }
  if (isCustomerDirectoryUpdated(message)) {
    const salesPointId = readSalesPointIdFromRealtimeMessage(message);
    if (salesPointId !== undefined) {
      invalidateShopKioskMetaForSalesPoint(queryClient, salesPointId);
    } else {
      invalidateAllShopKioskMetaCache(queryClient);
    }
  }
}

function directoryUpdatePayload(
  message: DirectoryEventMessage,
): Record<string, unknown> | undefined {
  if (message.type === 'customer_directory_updated') {
    return message.data;
  }
  if (message.data?.type === 'customer_directory_updated') {
    return message.data;
  }
  return undefined;
}

export function CustomerDirectoryGlobalSync(): null {
  const queryClient = useQueryClient();
  const { apiBaseUrl } = useRuntimeConfig();
  const queryClientRef = useRef(queryClient);
  const apiBaseUrlRef = useRef(apiBaseUrl);
  const salesPointsRefetchRef = useRef<CoalescedRefetchScheduler | null>(null);
  const tenantsRefetchRef = useRef<CoalescedRefetchScheduler | null>(null);
  const discoverySearchRefetchRef = useRef<CoalescedRefetchScheduler | null>(null);
  const lastSseRefreshRef = useRef(0);
  const lastVisibilityRefreshRef = useRef(0);
  const stormCatchUpPendingRef = useRef(false);

  useEffect(() => {
    queryClientRef.current = queryClient;
    apiBaseUrlRef.current = apiBaseUrl;
  }, [queryClient, apiBaseUrl]);

  useEffect(() => {
    if (salesPointsRefetchRef.current === null) {
      salesPointsRefetchRef.current = createCoalescedRefetchScheduler(
        () => queryClientRef.current,
        () => ['public', 'customer-sales-points', apiBaseUrlRef.current],
      );
    }
    if (tenantsRefetchRef.current === null) {
      tenantsRefetchRef.current = createCoalescedRefetchScheduler(
        () => queryClientRef.current,
        () => customerQueryKeys.publicCustomerTenants(apiBaseUrlRef.current),
      );
    }
    if (discoverySearchRefetchRef.current === null) {
      discoverySearchRefetchRef.current = createCoalescedRefetchScheduler(
        () => queryClientRef.current,
        () => ['public', 'discovery', 'search', apiBaseUrlRef.current],
      );
    }
    const salesPointsRefetch = salesPointsRefetchRef.current;
    const tenantsRefetch = tenantsRefetchRef.current;
    const discoverySearchRefetch = discoverySearchRefetchRef.current;
    return (): void => {
      salesPointsRefetch.cancel();
      tenantsRefetch.cancel();
      discoverySearchRefetch.cancel();
    };
  }, []);

  useEffect(() => {
    if (isHttpStormLatched()) {
      stormCatchUpPendingRef.current = true;
    }
    return subscribeHttpStormLatch(() => {
      if (isHttpStormLatched()) {
        stormCatchUpPendingRef.current = true;
      }
    });
  }, []);

  const scheduleDirectoryRefresh = useCallback((): void => {
    salesPointsRefetchRef.current?.schedule();
    tenantsRefetchRef.current?.schedule();
    discoverySearchRefetchRef.current?.schedule();
    // G7 — tab-return + SSE connect catch-up: memberships logo/rim (no tenant/customer in scope).
    invalidateAllAccountMembershipsCache(queryClientRef.current);
    // G12 — entitlements, checkout pickup lists, promo tenant caches.
    scheduleDirectoryDependentCaches(queryClientRef.current);
  }, []);

  /** G6 — zero-debounce flush when geo patch misses loaded cache pages. */
  const scheduleUrgentDirectoryRefresh = useCallback((): void => {
    void salesPointsRefetchRef.current?.flush();
    void discoverySearchRefetchRef.current?.flush();
    tenantsRefetchRef.current?.schedule();
    invalidateAllAccountMembershipsCache(queryClientRef.current);
    scheduleDirectoryDependentCaches(queryClientRef.current);
  }, []);

  const isConnected = usePublicDirectorySseConnected();

  useEffect(() => {
    if (!isConnected) {
      return;
    }
    const now = Date.now();
    const stormBypass = stormCatchUpPendingRef.current;
    if (stormBypass) {
      stormCatchUpPendingRef.current = false;
    } else if (now - lastSseRefreshRef.current < SSE_DIRECTORY_REFRESH_COOLDOWN_MS) {
      return;
    }
    lastSseRefreshRef.current = now;
    scheduleDirectoryRefresh();
  }, [isConnected, scheduleDirectoryRefresh]);

  useEffect(() => {
    const scheduleOnTabReturn = (): void => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      const now = Date.now();
      if (now - lastVisibilityRefreshRef.current < VISIBILITY_DIRECTORY_REFRESH_COOLDOWN_MS) {
        return;
      }
      lastVisibilityRefreshRef.current = now;
      scheduleDirectoryRefresh();
    };

    document.addEventListener('visibilitychange', scheduleOnTabReturn);
    window.addEventListener('focus', scheduleOnTabReturn);

    const handleWebSocketMessage = (event: Event): void => {
      try {
        const customEvent = event as CustomEvent<{ data: string }>;
        const raw = customEvent.detail?.data;
        if (typeof raw !== 'string' || raw.length === 0) {
          return;
        }
        const message = JSON.parse(raw) as DirectoryEventMessage;
        if (
          !isCustomerDirectoryUpdated(message) &&
          !isTenantBrandingUpdated(message) &&
          !isTenantPromotionsConfigUpdated(message) &&
          !isPickupPointsUpdated(message) &&
          !isKioskUpdated(message)
        ) {
          return;
        }

        if (isPickupPointsUpdated(message)) {
          const salesPointId = readSalesPointIdFromRealtimeMessage(message);
          if (salesPointId !== undefined) {
            invalidateKioskPickupPointsForSalesPoint(queryClientRef.current, salesPointId);
          } else {
            invalidateAllKioskPickupPointsCache(queryClientRef.current);
          }
          return;
        }

        if (isCustomerDirectoryUpdated(message)) {
          const payload = directoryUpdatePayload(message);
          const geo = readDirectoryGeoFromPayload(payload);
          const displayFields = readDirectoryDisplayFieldsFromPayload(payload);
          const salesPointId = readDirectorySalesPointId(payload);
          let geoPatchApplied = false;
          if (geo !== null && salesPointId !== null) {
            geoPatchApplied = patchDirectoryGeoInCaches(
              queryClientRef.current,
              apiBaseUrlRef.current,
              salesPointId,
              geo.lat,
              geo.lng,
            );
          }
          if (displayFields !== null && salesPointId !== null) {
            patchDirectoryDisplayInCaches(
              queryClientRef.current,
              apiBaseUrlRef.current,
              salesPointId,
              displayFields,
            );
          }
          if (geo !== null && salesPointId !== null && !geoPatchApplied) {
            scheduleUrgentDirectoryRefresh();
            invalidateAllSalesPointContactsCache(queryClientRef.current);
            invalidateAllAccountMembershipsCache(queryClientRef.current);
            refreshShopKioskMetaFromDirectoryEvent(queryClientRef.current, message);
            return;
          }
        }

        invalidateAllSalesPointContactsCache(queryClientRef.current);
        invalidateAllAccountMembershipsCache(queryClientRef.current);
        refreshShopKioskMetaFromDirectoryEvent(queryClientRef.current, message);
        scheduleDirectoryRefresh();
      } catch {
        return;
      }
    };

    window.addEventListener('websocket-message', handleWebSocketMessage);
    return (): void => {
      document.removeEventListener('visibilitychange', scheduleOnTabReturn);
      window.removeEventListener('focus', scheduleOnTabReturn);
      window.removeEventListener('websocket-message', handleWebSocketMessage);
    };
  }, [scheduleDirectoryRefresh, scheduleUrgentDirectoryRefresh]);

  return null;
}
